import {
  Pin,
  PinComponent,
  PinComponentOfType,
  PinTheme,
  PinThemeAnswerCard,
  PinThemeEntry,
  PinThemePinComponentSettingsType,
  PinThemePinSettingsType,
  PinType,
  TypographySettings,
  TypographyType,
  TypographyTypeSchema,
} from '@lessonup/pins-shared';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { defaultPinTheme } from './defaultPinTheme';
import { getPinThemeSettings } from './pinTheme.utils';
import { getPinThemeAnserCard } from './pinThemeAnswerCardHandler';
import { getPinThemePinComponentSettingsHandlers } from './pinThemePinComponentSettingsHandler';
import { PinThemePinComponentSettingsHandlerParams } from './pinThemePinComponentSettingsHandler.utils';
import { getPinThemePinSettingsHandlers } from './pinThemePinSettingsHandler';
import { getPinThemeTypography } from './pinThemeTypographyHandler';

type PinThemeContextProps = {
  answerCards: PinThemeAnswerCard;
  pinTheme: PinTheme;
  pinThemeEntry: PinThemeEntry;
  typographies: Record<TypographyType, TypographySettings>;
  getPinSettings: <P extends Pin, T extends PinThemePinSettingsType & P['type']>(
    pinType: T,
    settings: P['settings']
  ) => P['settings'];
  getPinComponentSettings: <P extends PinComponent, T extends PinThemePinComponentSettingsType & P['type']>(
    pinComponentType: T,
    params: PinThemePinComponentSettingsHandlerParams<T>
  ) => PinComponentOfType<T>['settings'];
};

const PinThemeContext = createContext<PinThemeContextProps | null>(null);

export const PinThemeContextProvider: React.FC<React.PropsWithChildren<{ pinType: PinType; pinTheme?: PinTheme }>> = ({
  pinType,
  pinTheme = defaultPinTheme,
  children,
}) => {
  const typographies = useMemo(
    () =>
      TypographyTypeSchema.options.reduce(
        (acc, typographyKey) => ({
          ...acc,
          [typographyKey]: getPinThemeTypography(pinTheme, typographyKey),
        }),
        {} as Record<TypographyType, TypographySettings>
      ),
    [pinTheme]
  );

  const pinThemeEntry = useMemo(() => getPinThemeSettings(pinTheme, pinType), [pinTheme, pinType]);

  const answerCards = useMemo(() => getPinThemeAnserCard(pinThemeEntry), [pinThemeEntry]);

  const getPinSettings = useCallback(
    <P extends Pin, T extends PinThemePinSettingsType & P['type']>(
      pinType: T,
      settings: P['settings']
    ): P['settings'] => getPinThemePinSettingsHandlers(pinTheme)(pinType, settings),
    [pinTheme]
  );

  const pinComponentSettingsHandlers = useMemo(() => {
    return getPinThemePinComponentSettingsHandlers(pinThemeEntry, pinTheme.typography);
  }, [pinThemeEntry, pinTheme]);

  const getPinComponentSettings = useCallback(
    <P extends PinComponent, T extends PinThemePinComponentSettingsType & P['type']>(
      pinComponentType: T,
      params: PinThemePinComponentSettingsHandlerParams<T>
    ): PinComponentOfType<T>['settings'] => pinComponentSettingsHandlers[pinComponentType](params),
    [pinComponentSettingsHandlers]
  );

  const contextValue = useMemo(
    () => ({
      answerCards,
      pinTheme,
      pinThemeEntry,
      getPinSettings,
      getPinComponentSettings,
      typographies,
    }),
    [answerCards, pinTheme, pinThemeEntry, getPinSettings, getPinComponentSettings, typographies]
  );

  return <PinThemeContext.Provider value={contextValue}>{children}</PinThemeContext.Provider>;
};

export function usePinThemeContext() {
  const context = useContext(PinThemeContext);
  if (context === null) {
    throw new Error('usePinTheme must be used within a PinThemeContextProvider');
  }

  return context;
}

/**
 * @returns Returns the PinThemePinComponentSettings entry for the provided PinComponentType
 */
export function useEffectivePinComponentSettings<
  P extends PinComponent,
  T extends PinThemePinComponentSettingsType & P['type'],
>(pinComponentType: T, params: PinThemePinComponentSettingsHandlerParams<T>): PinComponentOfType<T>['settings'] {
  const { getPinComponentSettings } = usePinThemeContext();

  return getPinComponentSettings(pinComponentType, params);
}

export function useEffectPinSettings<P extends Pin, T extends PinThemePinSettingsType & P['type']>(
  pinType: T,
  params: P['settings']
) {
  const { getPinSettings } = usePinThemeContext();

  return getPinSettings(pinType, params);
}
